// Phase 5C — Recent captures chip strip.
// Shows up to ~5 recent vendor captures stored in localStorage (browser only).
// Clicking a chip prefills the modal — user still confirms before saving.
//
// Pure client-side convenience: no DB, no analytics, no learning.

import { Button } from '@/components/ui/button';
import { History, X } from 'lucide-react';
import type { RecentCapture } from '@/lib/parts-assist/recentCapturesStore';

interface Props {
  items: RecentCapture[];
  onPick: (entry: RecentCapture) => void;
  onClear: () => void;
}

export function RecentCaptures({ items, onPick, onClear }: Props) {
  if (!items.length) return null;
  const visible = items.slice(0, 5);

  return (
    <div className="rounded-md border border-dashed bg-muted/10 px-3 py-2 space-y-1.5">
      <div className="flex items-center justify-between text-[10px] uppercase tracking-wide text-muted-foreground">
        <span className="flex items-center gap-1">
          <History className="h-3 w-3" /> Recent captures
        </span>
        <button
          type="button"
          onClick={onClear}
          className="hover:text-foreground transition-colors"
        >
          Clear
        </button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {visible.map((e, i) => {
          const cost = e.unit_cost != null ? `$${Number(e.unit_cost).toFixed(2)}` : '—';
          const label = e.description.length > 28 ? e.description.slice(0, 28) + '…' : e.description;
          return (
            <Button
              key={`${e.captured_at}-${i}`}
              type="button"
              variant="outline"
              size="sm"
              className="h-6 px-2 text-[10px] gap-1"
              onClick={() => onPick(e)}
              title={`${e.description}${e.part_number ? ` · ${e.part_number}` : ''} · ${cost}`}
            >
              <span className="truncate max-w-[180px]">{label}</span>
              <span className="text-muted-foreground">{cost}</span>
            </Button>
          );
        })}
      </div>
    </div>
  );
}
